import { useCallback, useMemo } from "react";

import type { Tag } from "../types/types";
import { getPopularTags } from "../utils/tagUtils";

import { normalizeForSearch, searchIncludes } from "@/utils/normalizeForSearch";
import { validateTagName } from "@/utils/tagFiltering";

/**
 * Props for useTagSearch hook
 */
interface UseTagSearchProps<T extends Tag = Tag> {
  /**
   * Array of all available tags to search through
   */
  tags: T[];
  /**
   * Current search query string entered by user
   */
  searchQuery: string;
  /**
   * Function to update the search query
   */
  setSearchQuery: (query: string) => void;
}

/**
 * Hook that handles search, filtering, and validation logic for tag operations
 * @param props - The hook props
 * @param props.tags - Array of all available tags to search through
 * @param props.searchQuery - Current search query string entered by user
 * @param props.setSearchQuery - Function to update the search query
 * @returns Object with filtered tags, exact match flag, canCreateNew flag, and search handlers
 */
function useTagSearch<T extends Tag = Tag>({
  tags,
  searchQuery,
  setSearchQuery,
}: UseTagSearchProps<T>) {
  // Search and filtering logic
  const filteredTags = useMemo<T[]>(() => {
    if (!searchQuery.trim()) {
      // Show popular tags first when not searching
      const popular = getPopularTags(tags, 5) as T[];
      const others = tags.filter(tag => !popular.some(p => p.id === tag.id));
      return [...popular, ...others];
    }

    return tags.filter(tag => searchIncludes(tag.name, searchQuery));
  }, [tags, searchQuery]);

  // Validation logic - use accent-insensitive comparison for exact match
  const exactMatch = useMemo(() => {
    const normalizedQuery = normalizeForSearch(searchQuery);
    return tags.find(tag => normalizeForSearch(tag.name) === normalizedQuery);
  }, [tags, searchQuery]);

  const canCreateNew = Boolean(searchQuery.trim() && !exactMatch);

  const validateSearchQuery = useCallback((query: string) => {
    if (!query.trim()) {
      return { isValid: false, error: "Tag name cannot be empty" };
    }
    return validateTagName(query.trim());
  }, []);

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
    },
    [setSearchQuery]
  );

  return {
    // State
    filteredTags,
    exactMatch,
    canCreateNew,

    // Handlers
    handleSearchQueryChange,
    validateSearchQuery,
  };
}

export default useTagSearch;
